import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Check, X, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SectionRenameProps {
  section: {
    id: string;
    name: string;
    section_type: 'DOCUMENTS' | 'FAQ';
  };
  onSectionRenamed: (sectionId: string, newName: string) => void;
  mandatorySections: string[];
}

export const SectionRename = ({ section, onSectionRenamed, mandatorySections }: SectionRenameProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(section.name);
  const [isLoading, setIsLoading] = useState(false);
  const isMandatory = mandatorySections.includes(section.name);

  const handleSave = async () => {
    if (!newName.trim() || newName.trim() === section.name) {
      setIsEditing(false);
      setNewName(section.name);
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('document_sections')
        .update({ name: newName.trim() })
        .eq('id', section.id);

      if (error) throw error;

      toast.success('Section renamed successfully');
      onSectionRenamed(section.id, newName.trim());
      setIsEditing(false);
    } catch (error: any) {
      console.error('Error renaming section:', error);
      toast.error(error.message || 'Failed to rename section');
      setNewName(section.name); // Reset to original name
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNewName(section.name);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="h-8 text-sm"
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          autoFocus
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={handleSave}
          disabled={isLoading}
          className="h-8 w-8 p-0"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCancel}
          disabled={isLoading}
          className="h-8 w-8 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <span className="text-sm font-bold leading-normal tracking-[0.015em]">
        {section.name}
      </span>
      {!isMandatory && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsEditing(true)}
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Edit className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};